// Mount / riding system (MB.7, E-14). PURE (ENG-1): where a passenger sits on a
// vehicle, how many riders a vehicle holds, which mounts steer to their rider's
// look, and the entity-vs-entity push-apart impulse that keeps mobs from stacking.
// The host binds a passenger to a vehicle and, each tick, snaps the passenger to
// passengerPosition() and applies the shove impulse; all the geometry is here.

export type VehicleKind = 'minecart' | 'boat' | 'horse' | 'llama' | 'pig' | 'strider' | 'donkey' | 'mule' | 'camel' | 'helicopter' | 'van';

export interface Vehicle {
  kind: VehicleKind;
  x: number; y: number; z: number;
  yaw: number;
}

/** Seat height above the vehicle's origin, per kind (approx. Java mount offsets). */
export const SEAT_OFFSET: Record<VehicleKind, number> = {
  minecart: 0.35, boat: 0.1, horse: 1.0, donkey: 0.95, mule: 0.95, llama: 1.1, pig: 0.6, strider: 1.1, camel: 1.7,
  helicopter: 0.6, van: 0.8, // SAR vehicles (M36.3)
};

/** How many passengers a vehicle carries (boats seat two; the SAR heli seats 4 =
 *  pilot + 3, the van 8). */
export const SEAT_CAPACITY: Record<VehicleKind, number> = {
  minecart: 1, boat: 2, horse: 1, donkey: 1, mule: 1, llama: 1, pig: 1, strider: 1, camel: 2,
  helicopter: 4, van: 8, // SAR vehicles (M36.3, SAR-5)
};

/** Where each seat of a multi-seat vehicle sits ALONG its facing, relative to the
 *  origin. Seat 0 stays exactly at the origin — that is where the single rider has
 *  always sat, and moving it would shift every existing boat/camel ride — and later
 *  seats sit BEHIND it, so a camel's two riders no longer occupy the same block.
 *  Single-seat kinds are absent here and fall back to 0. */
export const SEAT_FORWARD: Partial<Record<VehicleKind, readonly number[]>> = {
  boat: [0, -0.8],
  camel: [0, -0.9],
};

/** World position a passenger in `seat` is snapped to while riding `v`. Seat 0 is
 *  the default (and the only seat most vehicles have). */
export function passengerPosition(v: Vehicle, seat = 0): { x: number; y: number; z: number } {
  const fwd = SEAT_FORWARD[v.kind]?.[seat] ?? 0;
  // The mob yaw convention is `yaw = atan2(-x, -z)`, so forward is (−sin, −cos).
  return { x: v.x - Math.sin(v.yaw) * fwd, y: v.y + SEAT_OFFSET[v.kind], z: v.z - Math.cos(v.yaw) * fwd };
}

/** Can another rider board this vehicle right now? */
export function canMount(kind: VehicleKind, currentRiders: number): boolean {
  return currentRiders < SEAT_CAPACITY[kind];
}

/** A saddled mount steers toward the rider's look (horse/pig/strider/donkey/mule);
 *  a llama needs no saddle but only follows in a caravan, never steers. */
export function isSteerable(kind: VehicleKind, saddled: boolean): boolean {
  if (kind === 'llama' || kind === 'minecart' || kind === 'boat') return false;
  return saddled;
}

/** The mount VehicleKind a rideable mob maps to (horse/pig/camel/…), or null if the
 *  mob can't be ridden (M19 land-mount riding). */
const MOUNT_KINDS: Record<string, VehicleKind> = {
  horse: 'horse', donkey: 'donkey', mule: 'mule', pig: 'pig', strider: 'strider', camel: 'camel',
};
export function mountKindOf(mobType: string): VehicleKind | null {
  return MOUNT_KINDS[mobType] ?? null;
}

/** The wish velocity a ridden mount takes from forward input (+1 ahead, −1 back) at
 *  `speed` blocks/tick, facing `yaw` — matching the mob yaw convention
 *  (`yaw = atan2(-wishX, -wishZ)`), so the mount turns to face where it's driven. */
export function mountWish(forward: number, yaw: number, speed: number): { wishX: number; wishZ: number } {
  const s = speed * forward;
  return { wishX: -Math.sin(yaw) * s, wishZ: -Math.cos(yaw) * s };
}

/** Control item that makes a mount go: a pig follows a carrot on a stick, a strider
 *  a WARPED FUNGUS on a stick (its nether cousin), and the rest steer on the
 *  saddle's own reins. Null when the mount isn't rider-driven at all. */
export function controlItem(kind: VehicleKind): string | null {
  if (kind === 'pig') return 'carrot_on_a_stick';
  if (kind === 'strider') return 'warped_fungus_on_a_stick';
  if (kind === 'horse' || kind === 'donkey' || kind === 'mule' || kind === 'camel') return 'saddle';
  return null;
}

/** Does a mount of this kind need its control item HELD to be driven? A pig or a
 *  strider goes where the stick points; a saddled horse steers on its reins, which
 *  the rider is already holding by definition. */
export function needsHeldControl(kind: VehicleKind): boolean {
  return kind === 'pig' || kind === 'strider';
}

export interface Shovable { x: number; z: number; }

/**
 * Push-apart impulse for entity `a` away from `b` when their cylinders overlap
 * within `radius` (E-14 entity collision). Strength scales with penetration depth,
 * matching Java's soft push. Returns a zero impulse when they aren't overlapping
 * (or are exactly coincident — the host jitters those apart separately).
 */
export function shoveImpulse(a: Shovable, b: Shovable, radius: number, strength = 0.05): { x: number; z: number } {
  const dx = a.x - b.x, dz = a.z - b.z;
  const d = Math.hypot(dx, dz);
  if (d === 0 || d >= radius) return { x: 0, z: 0 };
  const push = ((radius - d) / radius) * strength;
  return { x: (dx / d) * push, z: (dz / d) * push };
}

// ── Mob passengers (M62) ─────────────────────────────────────────────────────
/** How close a mob must blunder to a vehicle before it climbs aboard. */
export const MOB_BOARD_RADIUS = 1.0;

/** Kinds a wandering mob will board on its own. A mob shoves itself into a boat
 *  or a minecart in Minecraft; it does not spontaneously saddle a horse. */
const MOB_BOARDABLE: ReadonlySet<VehicleKind> = new Set<VehicleKind>(['boat', 'minecart']);

/**
 * Does a mob at (mx,my,mz) climb into this vehicle? True when the kind is one a
 * mob boards at all, the vehicle has a seat free, and the mob is close enough —
 * which is the whole of Minecraft's rule, since a mob boards by bumping into one
 * rather than by deciding to.
 */
export function mobBoards(
  kind: VehicleKind,
  v: { x: number; y: number; z: number },
  mx: number, my: number, mz: number,
  occupiedSeats: number,
): boolean {
  if (!MOB_BOARDABLE.has(kind)) return false;
  if (occupiedSeats >= SEAT_CAPACITY[kind]) return false;
  return Math.hypot(v.x - mx, v.y - my, v.z - mz) <= MOB_BOARD_RADIUS;
}

/** The lowest free seat of a vehicle, or null when it is full. */
export function freeSeat(kind: VehicleKind, taken: ReadonlySet<number>): number | null {
  for (let s = 0; s < SEAT_CAPACITY[kind]; s++) if (!taken.has(s)) return s;
  return null;
}
